import React, { useState, useEffect } from 'react';
import { 
  Search, FileText, Phone, Mail, Calendar, User, DollarSign,
  ChevronLeft, ChevronRight, Eye, MessageSquare, CheckCircle, XCircle, Clock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  nuevo: { label: 'Nuevo', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  contactado: { label: 'Contactado', color: 'bg-yellow-500/20 text-yellow-400', icon: Phone },
  en_negociacion: { label: 'En Negociación', color: 'bg-purple-500/20 text-purple-400', icon: MessageSquare },
  aprobado: { label: 'Aprobado', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  rechazado: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  convertido: { label: 'Convertido', color: 'bg-cyan-500/20 text-cyan-400', icon: CheckCircle },
  expirado: { label: 'Expirado', color: 'bg-zinc-500/20 text-zinc-400', icon: Clock }
};

const QuotesPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const limit = 20;

  const canEdit = hasPermission('quotes_edit');

  const loadQuotes = async () => {
    setLoading(true);
    try {
      let url = `/api/quotes?skip=${page * limit}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetchWithAuth(url);
      if (res.ok) {
        const data = await res.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error cargando presupuestos:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadQuotes();
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadQuotes();
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const res = await fetchWithAuth(`/api/quotes/${quoteId}/status?status=${newStatus}`, {
        method: 'PUT'
      });
      if (res.ok) {
        loadQuotes();
        if (selectedQuote?.id === quoteId) {
          setSelectedQuote(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error('Error actualizando estado:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PY').format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-PY', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Presupuestos</h1>
          <p className="text-zinc-400 text-sm">Cotizaciones generadas desde la calculadora</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, teléfono, código..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
            Buscar
          </button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="flex gap-6">
        {/* Quotes List */}
        <div className={`flex-1 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden ${selectedQuote ? 'hidden lg:block' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay presupuestos</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-zinc-800">
                {quotes.map((quote) => {
                  const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.nuevo;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div
                      key={quote.id}
                      onClick={() => setSelectedQuote(quote)}
                      className={`p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors ${
                        selectedQuote?.id === quote.id ? 'bg-zinc-800/50 border-l-2 border-emerald-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-white">{quote.name}</p>
                          <p className="text-xs text-zinc-500">{quote.quote_code}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatCurrency(quote.total_price_gs)} Gs
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(quote.created_at).split(',')[0]}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
                <p className="text-sm text-zinc-500">
                  Pág. {page + 1}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={quotes.length < limit}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Quote Detail */}
        {selectedQuote && (
          <div className="flex-1 lg:max-w-md bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-semibold text-white">Detalle del Presupuesto</h3>
              <button
                onClick={() => setSelectedQuote(null)}
                className="lg:hidden text-zinc-400 hover:text-white"
              >
                ← Volver
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
              {/* Cliente */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-xs font-medium text-zinc-400 uppercase mb-3">Cliente</h4>
                <p className="text-white font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-zinc-500" />
                  {selectedQuote.name}
                </p>
                <p className="text-sm text-zinc-400 flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {selectedQuote.phone}
                </p>
                {selectedQuote.email && (
                  <p className="text-sm text-zinc-400 flex items-center gap-2 mt-1">
                    <Mail className="w-4 h-4 text-zinc-500" />
                    {selectedQuote.email}
                  </p>
                )}
                {selectedQuote.address && (
                  <p className="text-sm text-zinc-400 mt-2">{selectedQuote.address}</p>
                )}
              </div>

              {/* Sistema */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-xs font-medium text-zinc-400 uppercase mb-3">Sistema Recomendado</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-zinc-500">Capacidad</p>
                    <p className="text-white font-medium">{selectedQuote.recommended_capacity_kw} kW</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Paneles</p>
                    <p className="text-white font-medium">{selectedQuote.recommended_panels} unidades</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Gasto Actual</p>
                    <p className="text-white font-medium">{formatCurrency(selectedQuote.monthly_spend_gs)} Gs</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Tipo</p>
                    <p className="text-white font-medium capitalize">{selectedQuote.property_type}</p>
                  </div>
                </div>
              </div>

              {/* Financiero */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-xs font-medium text-zinc-400 uppercase mb-3">Resumen Financiero</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Total</span>
                    <span className="text-white font-bold">{formatCurrency(selectedQuote.total_price_gs)} Gs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Ahorro Mensual</span>
                    <span className="text-emerald-400">{formatCurrency(selectedQuote.monthly_savings_gs)} Gs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Ahorro Anual</span>
                    <span className="text-emerald-400">{formatCurrency(selectedQuote.annual_savings_gs)} Gs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">ROI</span>
                    <span className="text-cyan-400">{selectedQuote.roi_years} años</span>
                  </div>
                  {selectedQuote.financing_selected && (
                    <div className="pt-2 mt-2 border-t border-zinc-700">
                      <p className="text-purple-400 text-xs">Financiamiento: {selectedQuote.financing_months} cuotas</p>
                      <p className="text-white">{formatCurrency(selectedQuote.monthly_payment_gs)} Gs/mes</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Estado */}
              {canEdit && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="text-xs font-medium text-zinc-400 uppercase mb-3">Cambiar Estado</h4>
                  <select
                    value={selectedQuote.status}
                    onChange={(e) => handleStatusChange(selectedQuote.id, e.target.value)}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                  >
                    {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${selectedQuote.phone.replace(/[^0-9]/g, '')}?text=Hola ${selectedQuote.name}, te escribimos de Zenex respecto a tu cotización ${selectedQuote.quote_code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Contactar por WhatsApp
              </a>

              <p className="text-xs text-zinc-500 text-center">
                Creado: {formatDate(selectedQuote.created_at)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotesPage;
